/**
 * Services Page - All service categories with full details
 * Redesigned for a high-end logistics and corporate visual style
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaShip, FaTruckMoving, FaWarehouse, FaLeaf,
  FaAnchor, FaTools, FaBoxes, FaIndustry, FaTint,
  FaCubes, FaWeightHanging, FaWind, FaFireAlt, FaTruck,
  FaFileContract, FaCarSide,
  FaBuilding, FaPlane, FaFileAlt, FaProjectDiagram, FaHandshake,
  FaBolt, FaThermometerHalf, FaMicrochip, FaChargingStation, FaSeedling
} from 'react-icons/fa';
import SEO from '../components/ui/SEO';
import { getServiceSchemas, getBreadcrumbSchema } from '../utils/schema';
import AnimatedSection from '../components/ui/AnimatedSection';
import ContactCTA from '../components/sections/ContactCTA';
import StaffingService from '../components/sections/StaffingService';

// Image imports
import shippingImg from '../assets/images/service-shipping-marine.png';
import projectImg from '../assets/images/service-project-cargo.png';
import logisticsImg from '../assets/images/service-integrated-logistics.png';
import greenImg from '../assets/images/service-green-energy.png';

// Service detail images
import shippingAgencyImg from '../assets/images/service-shipping-agency.png';
import vesselHusbandingImg from '../assets/images/service-vessel-husbanding.png';
import containerHandlingImg from '../assets/images/service-container-handling.png';
import bulkHandlingImg from '../assets/images/service-bulk-handling.png';
import tankerOperationsImg from '../assets/images/service-tanker-operations.png';
import projectCargoDetailImg from '../assets/images/service-project-cargo-detail.png';
import heavyLiftImg from '../assets/images/service-heavy-lift.png';
import windmillImg from '../assets/images/service-windmill.png';
import industrialEquipmentImg from '../assets/images/service-industrial-equipment.png';
import charteringImg from '../assets/images/service-chartering.png';
import carCarrierImg from '../assets/images/service-car-carrier.png';

// Category 03 detail images
import cfsImg from '../assets/images/service-cfs.png';
import yardImg from '../assets/images/service-yard.png';
import fleetImg from '../assets/images/service-fleet.png';
import forwardingImg from '../assets/images/service-forwarding.png';
import chaImg from '../assets/images/service-cha.png';
import supplyChainImg from '../assets/images/service-supply-chain.png';
import tplImg from '../assets/images/service-3pl.png';

// Category 04 detail images
import ampsImg from '../assets/images/service-amps.png';
import heatingImg from '../assets/images/service-heating.png';
import vfdImg from '../assets/images/service-vfd.png';
import electrificationImg from '../assets/images/service-electrification.png';
import advisoryImg from '../assets/images/service-advisory.png';

/* Icon mapping */
const iconMap = {
  FaAnchor, FaTools, FaBoxes, FaIndustry, FaTint,
  FaCubes, FaWeightHanging, FaWind, FaFireAlt, FaTruck,
  FaFileContract, FaCarSide,
  FaBuilding, FaWarehouse, FaTruckMoving, FaPlane, FaFileAlt,
  FaProjectDiagram, FaHandshake,
  FaBolt, FaThermometerHalf, FaMicrochip, FaChargingStation, FaSeedling
};

/* Data definitions */
const categories = [
  {
    id: 'shipping-marine',
    num: '01',
    icon: FaShip, title: 'Shipping & Marine Services',
    color: 'var(--orange)',
    image: shippingImg,
    description: 'Comprehensive shipping agency and marine services for all vessel types with absolute cargo coordination.',
    services: [
      { icon: 'FaAnchor', title: 'Shipping Agency', image: shippingAgencyImg, desc: 'Acting as full agents at port, managing custom clearances, owner representation, and documentation.', features: ['Port call coordination', 'Liner agency representation', 'Clearance & customs documentation', 'Vessel husbanding liaison'] },
      { icon: 'FaTools', title: 'Vessel Husbanding', image: vesselHusbandingImg, desc: 'Complete turn-key husbanding services managing crew changes, bunkering, stores, and medical stays.', features: ['Crew sign-on/sign-off services', 'Fresh water & provisions supply', 'Bunkering coordination', 'Spares clearance & delivery'] },
      { icon: 'FaBoxes', title: 'Container Ship Handling', image: containerHandlingImg, desc: 'Supervising stevedoring operations, cellular container slot plans, and terminal bookings.', features: ['Stevedore management', 'Slot coordination', 'Terminal operations management', 'Container yard liaison'] },
      { icon: 'FaIndustry', title: 'Bulk Carriers Handling', image: bulkHandlingImg, desc: 'Expert loading/discharging oversight for grain carriers, ore carriers, and dry bulk vessels.', features: ['Draft surveys management', 'Cargo gear certifications', 'Discharge coordination', 'Port trust compliance'] },
      { icon: 'FaTint', title: 'Tanker Ship Operations', image: tankerOperationsImg, desc: 'Managing liquid tanker operations, oil tankers, chemical vessels, and LPG terminals with high compliance.', features: ['Safety officer liaison', 'Pollution prevention compliance', 'Terminal pipeline coordination', 'Customs liquid manifests'] },
      { icon: 'FaTruck', title: 'ODC Transportation', image: projectImg, desc: 'Subcontinent-wide transport of over-dimensional consignments under statutory highway compliance.', features: ['ODC highway permits management', 'Police & safety escort liaison', 'Route obstruction clearing', 'Interstate clearance controls'] }
    ]
  },
  {
    id: 'logistics-project',
    num: '02',
    icon: FaTruckMoving, title: 'Logistics & Project Cargo',
    color: 'var(--orange)',
    image: projectImg,
    description: 'Handling complex heavy lifts, oversized out-of-gauge (OOG) machinery, and windmill components.',
    services: [
      { icon: 'FaCubes', title: 'Project Cargo Logistics', image: projectCargoDetailImg, desc: 'Comprehensive route mapping, crane planning, and multi-modal logistics for heavy machinery.', features: ['Pre-feasibility route surveys', 'Heavy lift cranes selection', 'Civil approvals & permissions', 'Site-to-site engineering control'] },
      { icon: 'FaWeightHanging', title: 'Heavy Lift Operations', image: heavyLiftImg, desc: 'Managing multi-axle trailers, hydraulic modular transport, and port heavy-lift coordination.', features: ['Hydraulic trailer operations', 'Modular transport logistics', 'Port dock load compliance', 'Lashing & securing calculations'] },
      { icon: 'FaWind', title: 'Windmill Cargo Handling', image: windmillImg, desc: 'Specialized blade, nacelle, and tower logistics from port terminals to remote installation sites.', features: ['Extra-long blade transporters', 'Nacelle heavy transport', 'Tower sections coordination', 'Wind farm delivery logistics'] },
      { icon: 'FaFireAlt', title: 'Industrial Equipment', image: industrialEquipmentImg, desc: 'Logistics for power plant boilers, gas turbines, pressure vessels, and industrial structures.', features: ['Pressure vessel clearance', 'Boiler shipping layouts', 'Turbine structural lashing', 'Factory site deliveries'] },
      { icon: 'FaCarSide', title: 'Car Carrier & RO-RO Logistics', image: carCarrierImg, desc: 'Operational handling of vehicle transport fleets, roll-on/roll-off machinery, port auto-yard management, and dealer shipments.', features: ['Auto-terminal tracking & PDI', 'Carrier loading & lashing control', 'RO-RO machinery & fleet transport', 'Dealership network dispatch'] },
      { icon: 'FaFileContract', title: 'Chartering & Brokering', image: charteringImg, desc: 'Voyage chartering, time charter negotiations, and global shipbroking representation for dry bulk, tanker, and project cargo.', features: ['Voyage & time charters', 'Brokerage & fixtures representation', 'Laytime & demurrage analysis', 'Bunker coordination & compliance'] }
    ]
  },
  {
    id: 'integrated-logistics',
    num: '03',
    icon: FaWarehouse, title: 'Integrated Logistics Solutions',
    color: 'var(--orange)',
    image: logisticsImg,
    description: 'Full-infrastructure logistics including warehousing, Container Freight Stations (CFS), and freight forwarding.',
    services: [
      { icon: 'FaBuilding', title: 'CFS Cargo Operations', image: cfsImg, desc: 'Container Freight Station consolidation, export stuffing, and import cargo de-stuffing services.', features: ['LCL cargo consolidation', 'FCL container stuffing', 'Bonded warehousing clearance', 'Yard container handling'] },
      { icon: 'FaWarehouse', title: 'Yard & Inventory Management', image: yardImg, desc: 'High-security warehouses, open yard storage, inventory audits, and distribution controls.', features: ['24/7 security & CCTV monitoring', 'Inventory audit reporting', 'Open storage yard logistics', 'WMS software integrations'] },
      { icon: 'FaTruckMoving', title: 'Fleet Distribution', image: fleetImg, desc: 'Subcontinent transport and distribution using dedicated road fleets and rail container pathways.', features: ['Road freight distribution', 'Rail container bookings', 'GPS fleet tracking access', 'Last-mile hub operations'] },
      { icon: 'FaPlane', title: 'Freight Forwarding', image: forwardingImg, desc: 'Global sea freight and air freight forwarding bookings with leading international ocean carriers.', features: ['Sea freight FCL & LCL', 'Air cargo bookings', 'Multi-carrier routing plans', 'Global agent liaisons'] },
      { icon: 'FaFileAlt', title: 'Customs Clearance (CHA)', image: chaImg, desc: 'Customs house agency services for import/export declaration filings and port clearances.', features: ['Customs bill of entry filings', 'HS classification analysis', 'Duty assessment liaison', 'Port gates cargo clearing'] },
      { icon: 'FaProjectDiagram', title: 'Supply Chain Operations', image: supplyChainImg, desc: 'Strategic consulting for multi-node logistics, hub-and-spoke models, and distribution networks.', features: ['Logistical route optimization', 'Cost reduction modeling', 'Vendor control management', 'Cargo transit KPIs'] },
      { icon: 'FaHandshake', title: '3PL Operations Management', image: tplImg, desc: 'Scalable third-party logistics agreements covering order packing, storage, and dispatch.', features: ['Order picking & packing', 'Kitting & labeling services', 'Reverse logistics tracking', 'Scalable logistics contracts'] }
    ]
  },
  {
    id: 'green-energy',
    num: '04',
    icon: FaLeaf, title: 'Green Energy Solutions',
    color: '#10B981',
    image: greenImg,
    description: 'Advanced port electrification and ashore marine power systems in partnership with Shanghai Hissen Engineering.',
    services: [
      { icon: 'FaBolt', title: 'Ashore Marine Power Systems (AMPS)', image: ampsImg, desc: 'High-voltage shore connection (cold ironing) enabling vessels to plug into port grids at berth.', features: ['Berth plug integration', 'High-voltage switchgear design', 'Vessel power synchronization', 'Emission reduction reporting'] },
      { icon: 'FaThermometerHalf', title: 'Fuel Electric Heating System', image: heatingImg, desc: 'Decarbonized electric heating systems for bulk tankers and liquid fuel storage terminals.', features: ['Bulk tanker electric heaters', 'Liquid terminal installations', 'Electrical control systems', 'Fuel efficiency monitoring'] },
      { icon: 'FaMicrochip', title: 'VFD Energy Saving', image: vfdImg, desc: 'Variable Frequency Drives optimizing port crane hoist motors and conveyor energy usage.', features: ['Cargo crane hoist drive tuning', 'Conveyor motor efficiency', 'Power factor correction', 'Harmonics suppression checks'] },
      { icon: 'FaChargingStation', title: 'Terminal Electrification', image: electrificationImg, desc: 'Heavy-duty charging networks for electric container tractors and terminal operations.', features: ['Heavy EV chargers deployment', 'Grid connection layouts', 'Fleet charging schedulers', 'Substation capacity management'] },
      // { icon: 'FaSeedling', title: 'Sustainable Port Advisory', image: advisoryImg, desc: 'Technical consulting and carbon audit modeling to achieve net zero container terminals.', features: ['Carbon footprint audit', 'Net zero roadmaps design', 'Renewable grid integrations', 'ESG compliance reporting'] }
    ]
  }
];

/* Custom Service Card */
const ServiceCard = ({ service, color, delay }) => {
  const IconComp = iconMap[service.icon] || FaShip;
  const hasImage = !!service.image;
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      whileHover={{ y: -4, borderColor: color }}
      style={{
        background: '#FFFFFF',
        borderRadius: 2,
        padding: hasImage ? '0 0 1.8rem 0' : '2rem 1.8rem',
        height: '100%',
        border: '1px solid rgba(10,17,40,0.05)',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'border-color 0.2s ease',
        overflow: 'hidden'
      }}
      className="service-detail-card"
    >
      <div>
        {hasImage && (
          <div style={{
            width: '100%',
            overflow: 'hidden',
            marginBottom: '1.25rem',
            borderBottom: '1px solid rgba(10,17,40,0.05)',
            position: 'relative'
          }}>
            <img
              src={service.image}
              alt={service.title}
              width={1024}
              height={1024}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          </div>
        )}
        <div style={{ padding: hasImage ? '0 1.8rem' : '0' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: 2,
            background: color === '#10B981' ? 'rgba(16,185,129,0.08)' : 'var(--orange-dim)',
            border: color === '#10B981' ? '1px solid rgba(16,185,129,0.22)' : '1px solid var(--orange-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem'
          }}>
            <IconComp size={14} color={color} />
          </div>
          <h5 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, color: 'var(--navy)', fontSize: '0.95rem', marginBottom: '0.6rem', letterSpacing: '-0.01em' }}>
            {service.title}
          </h5>
          <p style={{ color: 'var(--text-body)', fontSize: '0.82rem', lineHeight: '1.65', marginBottom: '1.25rem', textAlign: 'justify' }}>
            {service.desc}
          </p>
        </div>
      </div>

      <div style={{ padding: hasImage ? '0 1.8rem' : '0' }}>
        {service.features && (
          <ul style={{ padding: 0, margin: 0, borderTop: '1px solid rgba(10,17,40,0.04)', paddingTop: '1rem' }}>
            {service.features.map((f, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '0.3rem', lineHeight: 1.3 }}>
                <span style={{ color, fontSize: '0.55rem', flexShrink: 0 }}>—</span> {f}
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
};

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.isaacgroup.net/' },
    { name: 'Services', url: 'https://www.isaacgroup.net/services' }
  ]);

  return (
    <main style={{ paddingTop: '72px' }}>
      <SEO 
        title="Shipping & Logistics Services | ISAAC Logistics"
        description="Shipping, logistics, contract staffing and manpower supply in India. Explore marine services, project cargo, warehousing and green energy solutions."
        canonicalUrl="https://www.isaacgroup.net/services"
        schema={[...getServiceSchemas(), breadcrumbs]}
      />
      {/* Page Hero */}
      <section style={{
        padding: '4.5rem 0',
        background: 'var(--navy-deep)',
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid var(--orange-border)'
      }}>
        <div style={{
          position: 'absolute', top: 0, bottom: 0, left: '20%', width: '1px',
          background: 'rgba(255,255,255,0.02)', pointerEvents: 'none'
        }} />
        <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 1 }}>
          <AnimatedSection variant="fadeUp">
            <span className="section-tag">What We Offer</span>
            <h1 style={{ color: '#fff', marginTop: '0.75rem', marginBottom: '1.2rem', letterSpacing: '-0.025em', fontWeight: 800 }}>
              Our <span style={{ color: 'var(--orange)' }}>Services</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '600px', lineHeight: '1.8', fontSize: '1rem', marginBottom: '2rem', textAlign: 'justify' }}>
              Specialized divisions covering shipping agency operations, multimodal logistics, Container Freight Stations, and marine electrification systems.
            </p>
          </AnimatedSection>

          {/* Quick nav pills */}
          <div className="d-flex flex-wrap gap-2 mt-4 hero-quick-nav">
            {[{ id: 'contract-staffing', title: 'Contract Staffing & Manpower', icon: FaHandshake }, ...categories].map((c, i) => (
              <motion.a
                key={i} href={`#${c.id}`}
                whileHover={{ y: -1 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 2, padding: '0.5rem 1rem',
                  color: 'rgba(255,255,255,0.75)', textDecoration: 'none',
                  fontSize: '0.76rem', fontFamily: 'var(--font-heading)', fontWeight: 600,
                  letterSpacing: '0.5px', textTransform: 'uppercase'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(c.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="quick-nav-pill"
              >
                <c.icon size={11} color="var(--orange)" /> {c.title}
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      <StaffingService />

      {/* Service categories */}
      {categories.map((cat, ci) => (
        <section
          key={cat.id} id={cat.id}
          style={{
            padding: '4.5rem 0',
            background: ci % 2 === 0 ? '#FFFFFF' : 'var(--bg-page)',
            borderBottom: '1px solid rgba(10,17,40,0.05)',
            scrollMarginTop: '72px'
          }}
        >
          <div className="container-xxl px-4 px-lg-5">
            {/* Category header */}
            <AnimatedSection variant="fadeUp" className="mb-5" style={{ maxWidth: '750px', paddingLeft: '45px' }}>
              <div className="d-flex align-items-start gap-4 mb-3">
                <span style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 800,
                  fontSize: '1.8rem',
                  color: 'var(--orange)',
                  lineHeight: 1
                }}>{cat.num}</span>
                <div>
                  <h2 className="section-title" style={{ margin: 0, fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>
                    {cat.title}
                  </h2>
                  <p style={{ color: 'var(--text-body)', margin: '0.5rem 0 0 0', fontSize: '0.92rem', lineHeight: '1.7', textAlign: 'justify' }}>
                    {cat.description}
                  </p>
                </div>
              </div>
              <div className="section-divider" style={{ marginLeft: '45px', width: '40px', height: '1px', background: 'var(--orange)' }} />
            </AnimatedSection>

            {/* Service cards */}
            <div className="row g-4" style={{ paddingLeft: '45px' }}>
              {cat.services.map((s, si) => (
                <div key={si} className="col-sm-6 col-lg-4">
                  <ServiceCard service={s} color={cat.color} delay={si * 0.05} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <ContactCTA />

      <style>{`
        .service-detail-card:hover {
          border-color: var(--orange) !important;
        }
        .quick-nav-pill:hover {
          background: rgba(255,255,255,0.06) !important;
          border-color: var(--orange-border) !important;
          color: #fff !important;
        }
      `}</style>
    </main>
  );
};

export default Services;
